// Service avancé pour le tracking des visites et analytics - Compatible base galerie_tableaux
import { addLog, LOG_EVENTS } from './logService';
import ErrorHandler from './errorHandler.js';
import sessionManager from './sessionManager.js';
import viewTracker from './viewTracker.js';
// Bridge vers Umami / GA4 / Clarity (trackingService)
import {
  trackTableauView as umamiTrackTableauView,
  trackContactFormSubmit as umamiTrackContactFormSubmit,
  trackContactIntent as umamiTrackContactIntent,
} from './trackingService';

const ANALYTICS_KEY = 'analytics_data';
const VISIT_SESSION_KEY = 'visit_session';
const POPULAR_TABLEAUX_KEY = 'popular_tableaux';
const QUEUE_KEY = 'analytics_pending';
const ERRORS_KEY = 'analytics_errors';
// Endpoint backend pour stocker les données analytics
const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');
const ANALYTICS_ENDPOINT = `${API_BASE_URL}/api/analytics`;

// Configuration pour la reconnexion automatique
const RECONNECT_INTERVAL = 30000; // 30 secondes

class AnalyticsService {
  constructor() {
    // Utiliser les nouveaux gestionnaires
    this.sessionManager = sessionManager;
    this.viewTracker = viewTracker;

    this.sessionId = this.sessionManager.getSessionId();
    this.startTime = Date.now();
    this.pageViews = [];
    this.isOnline = false;
    this.pendingData = [];
    this.retryCount = 0;
    this.viewedTableaux = new Set();
    this.lastSyncAt = null;
    this.flushScheduled = false;
    this.maxRetriesPerItem = 5;
    this.loadPendingFromStorage();
    this.loadErrorsFromStorage();
    // Ne démarre pas le healthcheck en SSR ou dans les tests
    if (typeof window !== 'undefined') {
      this.startHealthCheck();
    }
  }

  // Génération d'un ID de session unique (délégué au sessionManager)
  generateSessionId() {
    return this.sessionManager.getSessionId();
  }

  // Vérification périodique de la connexion backend
  startHealthCheck() {
    setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/analytics/dashboard`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(5000),
        });

        if (response.ok && !this.isOnline) {
          this.isOnline = true;
          this.retryCount = 0;
          console.log('✅ Connexion backend MariaDB rétablie');
          await this.syncPendingData();
        } else if (!response.ok) {
          this.isOnline = false;
        }
      } catch (error) {
        if (this.isOnline) {
          ErrorHandler.logNetworkError(error, 'Analytics Health Check', {
            sessionId: this.sessionId,
            apiUrl: API_BASE_URL,
            retryCount: this.retryCount,
          });
          this.isOnline = false;
          this.retryCount++;
        }
      }
    }, RECONNECT_INTERVAL);
  }

  loadPendingFromStorage() {
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      if (raw) this.pendingData = JSON.parse(raw);
    } catch (_) { this.pendingData = []; }
  }
  loadErrorsFromStorage() {
    try {
      const raw = localStorage.getItem(ERRORS_KEY);
      if (raw) this.errorLog = JSON.parse(raw);
      else this.errorLog = [];
    } catch (_) { this.errorLog = []; }
  }
  persistQueue() {
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(this.pendingData)); } catch (e) { /* persistence locale ignorée (résilience) */ }
  }
  persistErrors() {
    try { localStorage.setItem(ERRORS_KEY, JSON.stringify(this.errorLog.slice(-100))); } catch (e) { /* persistence errors ignorée */ }
  }
  pushError(context, error, extra = {}) {
    const entry = { ts: Date.now(), context, message: error?.message || String(error), ...extra };
    this.errorLog.push(entry);
    this.persistErrors();
    this.dispatchUpdate();
  }
  clearErrors() { this.errorLog = []; this.persistErrors(); this.dispatchUpdate(); }
  queueData(action, data) {
    this.pendingData.push({ action, data, timestamp: Date.now(), attempts: 0 });
    this.persistQueue();
    this.scheduleFlush();
    this.dispatchUpdate();
  }
  scheduleFlush(delay = 1500) {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    setTimeout(() => { this.flushScheduled = false; this.flushQueue().catch(()=>{}); }, delay);
  }
  async flushQueue() {
    if (!this.isOnline || this.pendingData.length === 0) return;
    const remaining = [];
    for (const item of this.pendingData) {
      try {
        await this.saveToDatabase(item.action, item.data, false);
      } catch (e) {
        item.attempts = (item.attempts || 0) + 1;
        if (item.attempts < this.maxRetriesPerItem) remaining.push(item); else this.pushError('queue-drop', e, { action: item.action });
      }
    }
    this.pendingData = remaining;
    this.lastSyncAt = Date.now();
    this.persistQueue();
    this.dispatchUpdate();
  }
  dispatchUpdate() {
    try { window.dispatchEvent(new CustomEvent('analytics:updated')); } catch (e) { /* event dispatch ignoré */ }
  }
  // Synchronisation des données en attente
  async syncPendingData() {
    // override pour utiliser flushQueue
    try { await this.flushQueue(); } catch (e) { this.pushError('sync', e); }
  }

  // Initialisation de la session - Compatible avec analytics_sessions
  async initializeSession() {
    const sessionData = {
      sessionId: this.sessionId,
      startTime: this.startTime,
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'direct',
      screenResolution: `${screen.width}x${screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      isReturningVisitor: this.isReturningVisitor(),
      pageViews: 0,
      lastActivity: Date.now(),
      duration: 0,
    };

    // Sauvegarder localement
    localStorage.setItem(VISIT_SESSION_KEY, JSON.stringify(sessionData));

    // Sauvegarder dans la base de données MariaDB via l'API
    try {
      await this.saveToDatabase('session', sessionData);
      console.log('📊 Session analytics sauvegardée dans MariaDB:', this.sessionId);
    } catch (error) {
      console.debug('Session sauvegardée en mode local');
    }
  }

  // Méthode utilitaire pour sauvegarder dans MariaDB
  async saveToDatabase(action, data, addToPending = true) {
    try {
      let endpoint;
      let body;
      if (action === 'view' || action === 'interaction') {
        // Utiliser la route /api/views pour toutes les analytics
        endpoint = `${API_BASE_URL}/api/views`;
        // Fusionner les données interaction et vue
        body = JSON.stringify({
          tableauId: data.tableau_id || data.tableauId,
          sessionId: data.session_id || data.sessionId,
          durationSeconds: data.view_duration_seconds || data.durationSeconds || 0,
          interactionsCount: data.interactions_count || data.interactionsCount || 0,
          scrollDepth: data.scroll_depth || data.scrollDepth || 0,
          cameFrom: data.came_from || data.cameFrom || '',
          type: data.type || undefined,
          details: data.details || undefined,
        });
      } else if (action === 'session') {
        endpoint = `${API_BASE_URL}/api/analytics/session`;
        body = JSON.stringify({ action, data, timestamp: new Date().toISOString() });
      } else {
        endpoint = `${ANALYTICS_ENDPOINT}/${action}`;
        body = JSON.stringify({ action, data, timestamp: new Date().toISOString() });
      }
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
        signal: AbortSignal.timeout(10000), // Augmentation du timeout
      });

      if (!response.ok) {
        // Gestion spécifique des erreurs HTTP
        if (response.status === 404) {
          throw new Error(`Endpoint non trouvé: ${endpoint}`);
        } else if (response.status >= 500) {
          throw new Error(`Erreur serveur: ${response.status}`);
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }

      // Vérifier que la réponse est du JSON valide
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Réponse non-JSON reçue du serveur');
      }

      const result = await response.json();
      this.isOnline = true;
      this.retryCount = 0;

      if (!result.success && result.error) {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      this.isOnline = false;
      this.pushError(action, error, { offline: true });
      // Log détaillé pour le debugging
      console.warn(`📊 Analytics DB save failed (${action}):`, error.message);

      // Mode dégradé : sauvegarder localement
      if (addToPending) {
        this.pendingData.push({ action, data, timestamp: new Date().toISOString() });

        // Persistance des données en attente
        try {
          localStorage.setItem('analytics_pending', JSON.stringify(this.pendingData));
        } catch (storageError) {
          console.warn('Erreur sauvegarde localStorage:', storageError.message);
        }

        // Sauvegarder aussi dans le fallback
        try {
          const localData = JSON.parse(localStorage.getItem('analytics_fallback') || '[]');
          localData.push({ action, data, timestamp: new Date().toISOString() });
          localStorage.setItem('analytics_fallback', JSON.stringify(localData));
        } catch (fallbackError) {
          console.warn('Erreur sauvegarde fallback:', fallbackError.message);
        }
      }

      return { success: true, mode: 'local', error: error.message };
    }
  }

  /**
   * Démarre le tracking d'une vue de tableau (utilise le nouveau viewTracker)
   * + incrémente le compteur vues dans MariaDB (tableaux.vues)
   * + propage automatiquement vers Umami/GA4 via trackingService
   */
  startTableauView(tableauId, metadata = {}) {
    this.viewTracker.startView(tableauId, metadata);

    // ─── Incrément du compteur vues (tableaux.vues → admin dashboard) ───
    // Appel silencieux : n'interrompt pas l'UX en cas d'échec
    fetch(`${API_BASE_URL}/api/views/${tableauId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: AbortSignal.timeout(5000),
    }).catch(() => {/* hors-ligne : ignoré, viewTracker gère la queue */});

    // ─── Bridge Umami / GA4 ───────────────────────────────────────────
    umamiTrackTableauView({
      id: tableauId,
      titre: metadata.titre,
      categorie: metadata.categorie,
      prix: metadata.prix,
    });
  }

  /**
   * Termine le tracking de la vue actuelle
   */
  endTableauView() {
    return this.viewTracker.endView();
  }

  /**
   * Enregistre une interaction utilisateur
   */
  trackInteraction(type, details = {}) {
    this.viewTracker.recordInteraction(type, details);
  }

  /**
   * Suit l'envoi d'un formulaire de contact
   * Envoie vers MariaDB + Umami/GA4 en une seule fois
   * @param {object} details - données du contact (nameLength, messageLength, etc.)
   */
  trackContactSubmit(details = {}) {
    // MariaDB : interaction dans la vue actuelle
    this.viewTracker.recordInteraction('contact', details);
    // Bridge Umami / GA4
    umamiTrackContactFormSubmit();
  }

  /**
   * Suit un clic "Contacter l'artiste" depuis une popup tableau
   * @param {object} tableau - { id, titre }
   */
  trackContactIntent(tableau) {
    this.viewTracker.recordInteraction('contact_intent', { tableauId: tableau?.id });
    // Bridge Umami / GA4
    umamiTrackContactIntent(tableau);
  }

  /**
   * Obtient les données de la session actuelle
   */
  getSessionData() {
    return this.sessionManager.getSessionData();
  }

  /**
   * Obtient l'ID du visiteur unique
   */
  getVisitorId() {
    return this.sessionManager.getVisitorId();
  }

  /**
   * Vérifie si c'est un nouveau visiteur
   */
  isNewVisitor() {
    return this.sessionManager.isNewVisitor();
  }

  /**
   * Obtient les statistiques de la vue actuelle
   */
  getCurrentViewStats() {
    return this.viewTracker.getCurrentViewStats();
  }

  // Obtenir les informations utilisateur pour les analytics
  getUserInfo() {
    return {
      platform: navigator.platform || 'unknown',
      language: navigator.language || 'fr-FR',
      userAgent: navigator.userAgent || '',
      screenResolution: `${screen.width}x${screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  // Obtenir la description d'une interaction
  getInteractionDescription(interactionType) {
    const descriptions = {
      like: "J'aime",
      share: 'Partage',
      zoom: 'Zoom',
      download: 'Téléchargement',
      comment: 'Commentaire',
      contact: 'Contact',
    };
    return descriptions[interactionType] || 'Interaction';
  }

  // Récupérer les données analytics depuis MariaDB
  async getAnalyticsFromDatabase() {
    try {
      const response = await fetch(`${ANALYTICS_ENDPOINT}/dashboard`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Vérifier que la réponse est valide
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Vérifier le Content-Type avant de parser
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        if (textResponse.startsWith('<!DOCTYPE')) {
          throw new Error('Serveur a retourné une page HTML au lieu de JSON');
        }
        throw new Error(`Réponse non-JSON reçue. Content-Type: ${contentType}`);
      }

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('Erreur lors de la récupération des analytics:', error.message);
      return this.getAnalyticsData(); // Fallback vers les données locales
    }
  }

  // Récupérer les statistiques générales depuis la vue MariaDB
  async getGeneralStats() {
    try {
      const response = await fetch(`${ANALYTICS_ENDPOINT}/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Vérifier que la réponse est valide
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Vérifier le Content-Type avant de parser
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        if (textResponse.startsWith('<!DOCTYPE')) {
          throw new Error('Serveur a retourné une page HTML au lieu de JSON');
        }
        throw new Error(`Réponse non-JSON reçue. Content-Type: ${contentType}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des stats générales:', error.message);
      return {};
    }
  }

  // Récupérer les tableaux populaires depuis MariaDB
  async getPopularTableauxFromDB() {
    try {
      const response = await fetch(`${ANALYTICS_ENDPOINT}/popular-tableaux`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Vérifier que la réponse est valide
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Vérifier le Content-Type avant de parser
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        if (textResponse.startsWith('<!DOCTYPE')) {
          throw new Error('Serveur a retourné une page HTML au lieu de JSON');
        }
        throw new Error(`Réponse non-JSON reçue. Content-Type: ${contentType}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des tableaux populaires:', error.message);
      return this.getPopularTableaux(); // Fallback local
    }
  }

  // Récupérer les activités récentes depuis MariaDB
  async getRecentActivities(limit = 20) {
    try {
      const response = await fetch(`${ANALYTICS_ENDPOINT}/recent-activities?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Vérifier que la réponse est valide
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Vérifier le Content-Type avant de parser
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        if (textResponse.startsWith('<!DOCTYPE')) {
          throw new Error('Serveur a retourné une page HTML au lieu de JSON');
        }
        throw new Error(`Réponse non-JSON reçue. Content-Type: ${contentType}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la récupération des activités récentes:', error.message);
      return [];
    }
  }

  // Vérifier si c'est un visiteur récurrent
  isReturningVisitor() {
    const analyticsData = this.getAnalyticsData();
    return analyticsData.totalSessions > 0;
  }

  // Obtenir les tableaux populaires
  getPopularTableaux(limit = 10) {
    const popularData = JSON.parse(localStorage.getItem(POPULAR_TABLEAUX_KEY) || '{}');

    return Object.values(popularData)
      .map((tableau) => ({
        ...tableau,
        uniqueViews: tableau.uniqueViews.length,
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, limit);
  }

  // Obtenir les données analytics
  getAnalyticsData() {
    const data = localStorage.getItem(ANALYTICS_KEY);
    if (!data) {
      const initialData = {
        totalSessions: 0,
        totalPageViews: 0,
        totalTimeSpent: 0,
        firstVisit: Date.now(),
        lastVisit: Date.now(),
        recentViews: [],
        interactions: [],
        topCategories: {},
        hourlyStats: {},
        dailyStats: {},
        monthlyStats: {},
      };
      this.saveAnalyticsData(initialData);
      return initialData;
    }
    return JSON.parse(data);
  }

  // Sauvegarder les données analytics
  saveAnalyticsData(data) {
    data.lastVisit = Date.now();
    data.totalSessions = data.totalSessions || 0;
    data.totalPageViews = data.totalPageViews || 0;

    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(data));
  }

  // Obtenir les statistiques de session actuelle
  getCurrentSessionStats() {
    const sessionData = JSON.parse(localStorage.getItem(VISIT_SESSION_KEY) || '{}');
    return {
      ...sessionData,
      duration: Date.now() - (sessionData.startTime || Date.now()),
      isActive: Date.now() - (sessionData.lastActivity || 0) < 300000, // 5 minutes
    };
  }

  // Obtenir les statistiques détaillées
  getDetailedStats() {
    const analyticsData = this.getAnalyticsData();
    const popularTableaux = this.getPopularTableaux();
    const sessionStats = this.getCurrentSessionStats();

    return {
      overview: {
        totalSessions: analyticsData.totalSessions || 0,
        totalPageViews: analyticsData.totalPageViews || 0,
        totalTimeSpent: analyticsData.totalTimeSpent || 0,
        averageSessionDuration:
          analyticsData.totalSessions > 0
            ? analyticsData.totalTimeSpent / analyticsData.totalSessions
            : 0,
        bounceRate: this.calculateBounceRate(),
      },
      popularTableaux,
      recentActivity: (analyticsData.recentViews || []).slice(-20),
      currentSession: sessionStats,
      trends: this.calculateTrends(),
    };
  }

  // Calculer le taux de rebond
  calculateBounceRate() {
    const analyticsData = this.getAnalyticsData();
    const recentViews = analyticsData.recentViews || [];

    if (recentViews.length === 0) return 0;

    const sessions = {};
    recentViews.forEach((view) => {
      if (!sessions[view.sessionId]) sessions[view.sessionId] = [];
      sessions[view.sessionId].push(view);
    });

    const singlePageSessions = Object.values(sessions).filter(
      (session) => session.length === 1
    ).length;

    return (singlePageSessions / Object.keys(sessions).length) * 100;
  }

  // Calculer les tendances
  calculateTrends() {
    const analyticsData = this.getAnalyticsData();
    const recentViews = analyticsData.recentViews || [];
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;

    const today = recentViews.filter((view) => now - view.timestamp < dayMs);
    const yesterday = recentViews.filter(
      (view) => now - view.timestamp >= dayMs && now - view.timestamp < 2 * dayMs
    );

    return {
      viewsToday: today.length,
      viewsYesterday: yesterday.length,
      trend:
        yesterday.length > 0 ? ((today.length - yesterday.length) / yesterday.length) * 100 : 0,
    };
  }

  // Nettoyer les anciennes données
  cleanupOldData(maxAge = 30 * 24 * 60 * 60 * 1000) {
    // 30 jours par défaut
    const analyticsData = this.getAnalyticsData();
    const cutoff = Date.now() - maxAge;

    if (analyticsData.recentViews) {
      analyticsData.recentViews = analyticsData.recentViews.filter(
        (view) => view.timestamp > cutoff
      );
    }

    if (analyticsData.interactions) {
      analyticsData.interactions = analyticsData.interactions.filter(
        (interaction) => interaction.timestamp > cutoff
      );
    }

    this.saveAnalyticsData(analyticsData);
    console.log('🧹 Données anciennes nettoyées');
  }

  // Finaliser la session
  endSession() {
    try {
      const sessionData = this.getCurrentSessionStats();
      const analyticsData = this.getAnalyticsData();

      // Mettre à jour les totaux
      analyticsData.totalSessions += 1;
      analyticsData.totalPageViews += sessionData.pageViews || 0;
      analyticsData.totalTimeSpent += sessionData.duration || 0;

      this.saveAnalyticsData(analyticsData);
      localStorage.removeItem(VISIT_SESSION_KEY);

      console.log('🔚 Session terminée:', this.sessionId);
    } catch (e) { this.pushError('endSession', e); }
  }

  // Vérification de la connectivité avec gestion d'erreur améliorée
  async checkOnlineStatus() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${ANALYTICS_ENDPOINT}/dashboard`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        // Vérifier le Content-Type avant de parser
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const textResponse = await response.text();
          if (textResponse.startsWith('<!DOCTYPE')) {
            throw new Error('Serveur a retourné une page HTML au lieu de JSON');
          }
          throw new Error(`Réponse non-JSON reçue. Content-Type: ${contentType}`);
        }

        const data = await response.json();
        this.isOnline = data.healthy === true;
        this.retryCount = 0;
        return this.isOnline;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.debug('🔌 Analytics hors ligne:', error.message);
      this.isOnline = false;
      return false;
    }
  }

  // Ajout de la méthode updateScrollDepth pour éviter l'erreur
  updateScrollDepth(depth) {
    // Exemple : on pourrait envoyer la profondeur de scroll à l'API ou juste la logguer
    try {
      addLog(LOG_EVENTS.SCROLL_DEPTH, { sessionId: this.sessionId, depth });
      // Vous pouvez aussi ajouter une requête API ici si besoin
      // fetch(`${ANALYTICS_ENDPOINT}/scroll-depth`, { ... })
    } catch (error) {
      console.error('Erreur lors du tracking de la profondeur de scroll:', error);
    }
  }

  getDiagnosticSnapshot() {
    const grouped = {};
    (this.errorLog || []).forEach(err => {
      const key = err.context || 'inconnu';
      if (!grouped[key]) grouped[key] = { count: 0, lastTs: 0, lastMessage: '', samples: [] };
      const g = grouped[key];
      g.count++;
      if (err.ts > g.lastTs) { g.lastTs = err.ts; g.lastMessage = err.message; }
      if (g.samples.length < 5) g.samples.push({ ts: err.ts, message: err.message });
    });
    return {
      isOnline: this.isOnline,
      sessionId: this.sessionId,
      pendingCount: this.pendingData.length,
      retryCount: this.retryCount,
      lastSyncAt: this.lastSyncAt,
      errors: this.errorLog?.slice(-50) || [],
      groupedErrors: grouped,
      queueSample: this.pendingData.slice(0, 10),
      queueOldestTs: this.pendingData[0]?.timestamp || null,
      generatedAt: Date.now(),
    };
  }
}

// Instance singleton
const analyticsService = new AnalyticsService();

// Nettoyer automatiquement les anciennes données au démarrage
analyticsService.cleanupOldData();

// Finaliser la session lors de la fermeture de la page
window.addEventListener('beforeunload', () => {
  analyticsService.endSession();
});

export default analyticsService;
export function getAnalyticsDiagnostic() { return analyticsService.getDiagnosticSnapshot(); }
export function clearAnalyticsErrors() { analyticsService.clearErrors(); }
export function forceAnalyticsSync() { analyticsService.flushQueue(); }
